import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, Calendar, CheckCircle2, Mail, ShieldAlert, Sparkles } from 'lucide-react';
import api from '../../lib/api';
import { useAuthStore } from '../../stores/authStore';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import { Card, CardContent } from '../../components/ui/Card';
import { formatDate } from '../../lib/utils';
import { rememberInviteRedirect } from '../../lib/inviteRedirect';

type PublicInvitation = {
  id: string;
  urlSlug: string | null;
  status: string;
  createdAt: string;
  isClaimed: boolean;
  invitedKolName: string | null;
  invitedEmailMasked: string | null;
  /** Raw invited email for signup/login prefilling; null once profile is linked to an Auth user */
  inviteSignupEmail?: string | null;
  job: {
    title: string;
    description?: string | null;
    rewardsJson?: any;
    contractStart?: string | null;
    contractEnd?: string | null;
    brandOrg?: { name: string } | null;
    managedByOrg?: { name: string } | null;
  };
};

function rewardSummary(rewards: any): string {
  if (!rewards || typeof rewards !== 'object') return 'Campaign terms are listed inside the invitation.';
  const parts: string[] = [];
  if (Number(rewards.cash) > 0) parts.push(`RM ${Number(rewards.cash).toLocaleString()}`);
  if (rewards.product) parts.push(String(rewards.product));
  if (Number(rewards.commission) > 0) parts.push(`${Number(rewards.commission)}% commission`);
  return parts.length ? parts.join(' + ') : 'Campaign terms are listed inside the invitation.';
}

export default function InviteLanding() {
  const { id: rawId } = useParams();
  const inviteId = rawId ? decodeURIComponent(rawId) : '';
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [invite, setInvite] = useState<PublicInvitation | null>(null);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState('');

  const currentPath = useMemo(() => {
    if (typeof window === 'undefined') return inviteId ? `/invite/${encodeURIComponent(inviteId)}` : '/kol/dashboard';
    return `${window.location.pathname}${window.location.search}`;
  }, [inviteId]);

  const loginEmailSuffix = useMemo(() => {
    const email = invite?.inviteSignupEmail?.trim();
    if (!email) return '';
    return `&email=${encodeURIComponent(email)}`;
  }, [invite?.inviteSignupEmail]);

  const registerInviteSuffix = useMemo(() => {
    if (!invite) return '';
    const parts: string[] = [];
    const em = invite.inviteSignupEmail?.trim();
    if (em) parts.push(`email=${encodeURIComponent(em)}`);
    const nm = invite.invitedKolName?.trim();
    if (nm) parts.push(`prefillName=${encodeURIComponent(nm)}`);
    return parts.length ? `&${parts.join('&')}` : '';
  }, [invite]);

  useEffect(() => {
    if (!inviteId) return;
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const response = await api.get<PublicInvitation>(`/invitations/public/${encodeURIComponent(inviteId)}`);
        if (active) setInvite(response.data);
      } catch (err: any) {
        if (active) setError(err.response?.data?.error || 'Invitation not found');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [inviteId]);

  const openInvitation = async () => {
    if (!inviteId) return;
    if (!user) {
      rememberInviteRedirect(currentPath);
      const em = invite?.inviteSignupEmail?.trim();
      const qs = em ? `&email=${encodeURIComponent(em)}` : '';
      navigate(`/login?redirect=${encodeURIComponent(currentPath)}${qs}`);
      return;
    }
    if (user.role !== 'KOL') {
      setError('This invitation needs a KOL account. Sign out, then sign in with the invited creator email.');
      return;
    }
    try {
      setOpening(true);
      setError('');
      const response = await api.post<{ targetPath: string }>(`/invitations/${encodeURIComponent(inviteId)}/claim`);
      navigate(response.data.targetPath || `/kol/invitations/${encodeURIComponent(invite?.urlSlug || inviteId)}`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Could not open this invitation');
    } finally {
      setOpening(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-gray-600">Loading invitation...</div>
      </div>
    );
  }

  if (!invite) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="w-full max-w-lg">
          <CardContent className="p-8 text-center">
            <ShieldAlert className="mx-auto h-10 w-10 text-amber-500" />
            <h1 className="mt-4 text-2xl font-semibold text-gray-900">Invitation unavailable</h1>
            <p className="mt-2 text-sm text-gray-600">{error || 'This invitation link is invalid or has expired.'}</p>
            <Link to="/" className="mt-6 inline-flex text-sm font-medium text-blue-600 hover:underline">
              Back to KOL.my
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-cyan-50 px-4 py-10">
      <div className="mx-auto flex max-w-5xl flex-col gap-6">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-600 text-white">
            <Sparkles className="h-6 w-6" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-950">You have a campaign invitation</h1>
          <p className="mt-2 text-gray-600">
            {invite.isClaimed
              ? 'This creator profile is already linked to myKOL. Sign in with that account to open this invitation.'
              : 'Create your KOL account using the email the agency invited (below), then return here—or sign in if you already registered with that email.'}
          </p>
        </div>

        <Card className="overflow-hidden">
          <CardContent className="grid gap-0 p-0 md:grid-cols-[1.2fr_0.8fr]">
            <div className="space-y-5 p-8">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary">{invite.status}</Badge>
                {invite.isClaimed && <Badge variant="success">myKOL account linked</Badge>}
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-gray-950">{invite.job.title}</h2>
                <p className="mt-2 text-sm text-gray-600">
                  {invite.job.brandOrg?.name || 'Brand campaign'}
                  {invite.job.managedByOrg?.name ? ` managed by ${invite.job.managedByOrg.name}` : ''}
                </p>
              </div>
              {invite.job.description && (
                <p className="line-clamp-4 text-sm leading-6 text-gray-700">{invite.job.description}</p>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg border bg-gray-50 p-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                    <Mail className="h-4 w-4 text-emerald-600" />
                    Invited creator
                  </div>
                  <p className="mt-2 text-sm text-gray-600">
                    {invite.invitedKolName || 'Creator'} {invite.invitedEmailMasked ? `(${invite.invitedEmailMasked})` : ''}
                  </p>
                </div>
                <div className="rounded-lg border bg-gray-50 p-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                    <Calendar className="h-4 w-4 text-emerald-600" />
                    Timeline
                  </div>
                  <p className="mt-2 text-sm text-gray-600">
                    {invite.job.contractStart ? formatDate(invite.job.contractStart) : 'Start TBD'}
                    {' - '}
                    {invite.job.contractEnd ? formatDate(invite.job.contractEnd) : 'End TBD'}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col justify-center gap-4 bg-gray-950 p-8 text-white">
              <div className="rounded-lg border border-white/10 bg-white/5 p-4">
                <p className="text-xs uppercase tracking-wide text-white/50">Reward summary</p>
                <p className="mt-2 text-lg font-semibold">{rewardSummary(invite.job.rewardsJson)}</p>
              </div>

              {error && (
                <div className="rounded-lg border border-rose-300/30 bg-rose-500/10 p-3 text-sm text-rose-100">
                  {error}
                </div>
              )}

              {user ? (
                <>
                  <Button type="button" onClick={openInvitation} disabled={opening || user.role !== 'KOL'} className="w-full">
                    {opening ? 'Opening...' : 'Open invitation'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  {user.role !== 'KOL' && (
                    <button
                      type="button"
                      onClick={logout}
                      className="w-full rounded-lg border border-white/45 bg-white/10 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/20"
                    >
                      Sign out and use KOL account
                    </button>
                  )}
                </>
              ) : (
                <>
                  {invite.isClaimed ? (
                    <>
                      <Link
                        to={`/login?redirect=${encodeURIComponent(currentPath)}${loginEmailSuffix}`}
                        onClick={() => rememberInviteRedirect(currentPath)}
                      >
                        <Button type="button" className="w-full">
                          Sign in to review
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                      <p className="text-center text-xs text-white/55">
                        First time only applies before your profile is linked to myKOL. If you need to change the invited email,
                        ask your agency.
                      </p>
                    </>
                  ) : (
                    <>
                      <Link
                        to={`/register?role=KOL&redirect=${encodeURIComponent(currentPath)}${registerInviteSuffix}`}
                        onClick={() => rememberInviteRedirect(currentPath)}
                      >
                        <Button type="button" className="w-full">
                          Create KOL account
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                      <Link
                        to={`/login?redirect=${encodeURIComponent(currentPath)}${loginEmailSuffix}`}
                        onClick={() => rememberInviteRedirect(currentPath)}
                        className="flex w-full items-center justify-center rounded-lg border border-white/45 bg-white/10 px-4 py-3 text-center text-sm font-semibold text-white shadow-sm transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950"
                      >
                        Already registered? Sign in
                      </Link>
                    </>
                  )}
                </>
              )}

              <div className="flex items-start gap-2 text-xs text-white/60">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300" />
                Your account must use the same email the agency invited.
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
